import { supabase } from "../supabaseClient";
import { dbTypeToLocal, normalizeLocalType, dashboardRouteFromType } from "../utils/userTypes";
import { hasActiveSubscription } from "../features/payment/subscription/subscriptionStatus.js";

const getStoredUserType = () => normalizeLocalType(localStorage.getItem("user_type"));

// Fase 6.2: consome a URL de origem salva antes do login (ex.: perfil de babá
// que a família estava vendo). Retorna e limpa o valor.
const popRedirectAfterLogin = () => {
  try {
    const r = sessionStorage.getItem("redirect_after_login");
    if (r) sessionStorage.removeItem("redirect_after_login");
    return r;
  } catch {
    return null;
  }
};

const navigateFromType = (type, navigate) => {
  const route = dashboardRouteFromType(type);
  if (route) {
    navigate(route);
    return true;
  }
  return false;
};

const mapDbTypeToLocal = dbTypeToLocal;

const persistType = (type, setUserType) => {
  if (setUserType) setUserType(type);
  else localStorage.setItem("user_type", type);
};

export async function handleRedirect(userOrId, navigate, setUserType) {
  const userId = typeof userOrId === "string" ? userOrId : userOrId?.id;
  const userEmail =
    typeof userOrId === "object" && userOrId?.email
      ? String(userOrId.email).trim().toLowerCase()
      : null;

  if (!userId) {
    navigate("/login");
    return;
  }

  const pendingSignupRaw = localStorage.getItem("pendingSignup");
  if (pendingSignupRaw) {
    let pendingSignupData = null;
    try {
      pendingSignupData = JSON.parse(pendingSignupRaw);
    } catch {
      localStorage.removeItem("pendingSignup");
    }

    if (pendingSignupData) {
      const pendingUserId = pendingSignupData.userId;

      if (!pendingUserId || pendingUserId === userId) {
        localStorage.removeItem("pendingSignup");
        navigate("/perfil-novo");
        return;
      }

      localStorage.removeItem("pendingSignup");
    }
  }

  const { data: usuario, error: usuarioError } = await supabase
    .from("usuarios")
    .select("id, email, tipo, doc_status, status_conta, is_admin")
    .eq("id", userId)
    .single();

  if (usuarioError) {
    if (usuarioError.code === "PGRST116") {
      if (userEmail) {
        const { data: usuarioByEmail, error: usuarioByEmailError } = await supabase
          .from("usuarios")
          .select("id, email, tipo, doc_status, is_admin")
          .ilike("email", userEmail)
          .maybeSingle();

        if (!usuarioByEmailError && usuarioByEmail) {
          if (usuarioByEmail?.is_admin || usuarioByEmail?.tipo === "admin") {
            persistType("admin", setUserType);
            navigate("/dashboard-admin");
            return;
          }

          if (usuarioByEmail?.tipo) {
            const normalizedType = mapDbTypeToLocal(usuarioByEmail.tipo);
            if (normalizedType) {
              persistType(normalizedType, setUserType);
            }

            if (usuarioByEmail.tipo === "baba" || usuarioByEmail.tipo === "babysitter") {
              const { data: babaRow } = await supabase
                .from("usuarios_babas")
                .select("usuario_id")
                .eq("usuario_id", usuarioByEmail.id)
                .maybeSingle();

              if (!babaRow) {
                navigate("/cadastro-baba");
                return;
              }
            } else if (usuarioByEmail.tipo === "cliente" || usuarioByEmail.tipo === "familia") {
              const { data: clienteRow } = await supabase
                .from("usuarios_clientes")
                .select("usuario_id")
                .eq("usuario_id", usuarioByEmail.id)
                .maybeSingle();

              if (!clienteRow) {
                navigate("/cadastro-familia");
                return;
              }
            }

            if (navigateFromType(usuarioByEmail.tipo, navigate)) {
              return;
            }
          }
        }
      }

      const storedType = getStoredUserType();
      if (storedType === "babysitter") {
        navigate("/cadastro-baba");
        return;
      }
      if (storedType === "family") {
        navigate("/cadastro-familia");
        return;
      }

      navigate("/perfil-novo");
      return;
    }
    throw usuarioError;
  }

  if (usuario?.is_admin || usuario?.tipo === "admin") {
    persistType("admin", setUserType);
    navigate("/dashboard-admin");
    return;
  }

  if (usuario?.tipo) {
    const normalizedType = mapDbTypeToLocal(usuario.tipo);
    if (normalizedType) {
      persistType(normalizedType, setUserType);
    }

    // Para babás e famílias, verificar se o cadastro de perfil foi concluído
    // antes de mandar para o dashboard — evita dashboard vazio para contas novas.
    if (usuario.tipo === "baba" || usuario.tipo === "babysitter") {
      const { data: babaRow } = await supabase
        .from("usuarios_babas")
        .select("usuario_id")
        .eq("usuario_id", userId)
        .maybeSingle();

      if (!babaRow) {
        navigate("/cadastro-baba");
        return;
      }
    } else if (usuario.tipo === "cliente" || usuario.tipo === "familia") {
      const { data: clienteRow } = await supabase
        .from("usuarios_clientes")
        .select("usuario_id")
        .eq("usuario_id", userId)
        .maybeSingle();

      if (!clienteRow) {
        navigate("/cadastro-familia");
        return;
      }

      // Família cadastrada mas aguardando aprovação do admin
      if (usuario.doc_status !== "aprovado" || usuario.status_conta !== true) {
        navigate("/aguardando-aprovacao");
        return;
      }

      const assinaturaAtiva = await hasActiveSubscription({
        userId,
        email: userEmail || usuario.email,
      });

      if (!assinaturaAtiva) {
        navigate("/escolher-assinatura");
        return;
      }

      // Família aprovada e assinante: volta para a página de origem, se houver
      // (ex.: perfil de babá que estava vendo antes de logar).
      const redirect = popRedirectAfterLogin();
      if (redirect && redirect !== "/login") {
        navigate(redirect);
        return;
      }
    }

    if (navigateFromType(usuario.tipo, navigate)) {
      return;
    }
  }

  const storedType = getStoredUserType();
  if (storedType === "babysitter") {
    navigate("/cadastro-baba");
    return;
  }
  if (storedType === "family") {
    navigate("/cadastro-familia");
    return;
  }

  navigate("/perfil-novo");
}
